import { Dispatch, SetStateAction, useState, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import css from 'styled-jsx/css';

import useEvents from 'lib/hooks/useEvents';
import TrimTap from 'lib/Tracking/Srp/SearchFilters/TrimTap';

import LabeledCheckBox from 'components/Shared/SearchFilter/CheckBox/LabeledCheckBox';
import ScrollingContainer from 'components/ScrollingContainer';
import { colors } from 'components/styles/variables';

export interface TrimFilterProps {
  trims: string[];
  selectedTrims: string[];
  onAcceptClick: (trimValues: string[]) => void;
  setIsModalOpen?: Dispatch<SetStateAction<boolean>>;
}

export default function TrimFilter({
  trims,
  selectedTrims,
  onAcceptClick,
  setIsModalOpen,
}: TrimFilterProps): JSX.Element {
  const logger = useEvents();
  const { t } = useTranslation();

  const allTrims = t('srp.filters.trim.allTrims');

  const [trimValues, setTrimValues] = useState<string[]>(selectedTrims);

  function handleCancelClick(): void {
    setIsModalOpen?.(false);
  }

  function handleAcceptClick(): void {
    onAcceptClick(trimValues);
    setIsModalOpen?.(false);
  }

  function handleTrimChange(val: boolean, trimIndex: number): void {
    if (val) {
      // all trims selected
      if (trimIndex === 0) {
        setTrimValues(trims);
      } else {
        setTrimValues([...trimValues, trims[trimIndex]]);
      }
      // all trims deselected
    } else if (trimIndex === 0 && trimValues.includes(allTrims)) {
      setTrimValues([]);
    } else {
      let alltrimValues = trimValues;
      // deselect all trims when item gets deselected
      if (trimValues.includes(allTrims)) {
        alltrimValues = alltrimValues.filter((selected) => selected !== allTrims);
      }
      // deselect item
      const newSelected = alltrimValues.filter((selected) => selected !== trims[trimIndex]);
      setTrimValues(newSelected);
    }
  }

  const buttonStyles = `
    background-color: transparent;
    border: none;
    padding-left: 10px;
    padding-right: 16px;
    font-size: 14px;
  `;

  const scoped = css.resolve`
    .trim-container {
      padding-top: 8px;
      max-height: 465px;
      width: 294px;
    }

    .trim-buttons {
      display: flex;
      justify-content: flex-end;
      z-index: 1;
      position: sticky;
      bottom: 0;
      background-color: #fff;
      height: 44px;
      border-top: 1px solid #d3d3d3;
      padding-right: 5px;
      overflow: hidden;
      border-radius: 0 0 8px 8px;
    }

    .button-cancel {
      ${buttonStyles}
    }

    .button-accept {
      ${buttonStyles}
      color: ${colors.ALR1};
    }
  `;

  return (
    <ScrollingContainer>
      <div className={`${scoped.className} trim-container`}>
        {trims.map((trimLabel, trimIndex) => {
          return (
            <LabeledCheckBox
              key={trimLabel}
              label={trimLabel}
              aria-label={`${trimLabel}-label`}
              isChecked={trimValues?.includes(trims[trimIndex])}
              onClick={(value: boolean, event: MouseEvent<HTMLButtonElement>): void => {
                event.stopPropagation();
                logger.log(TrimTap());
                handleTrimChange(value, trimIndex);
              }}
            />
          );
        })}
        <div className={`${scoped.className} trim-buttons`}>
          <button className={`${scoped.className} button-cancel`} onClick={handleCancelClick}>
            {t('srp.filters.trim.cancel')}
          </button>
          <button className={`${scoped.className} button-accept`} onClick={handleAcceptClick}>
            {t('srp.filters.trim.accept')}
          </button>
        </div>
      </div>
      {scoped.styles}
    </ScrollingContainer>
  );
}
